import { z } from "zod";
import { FamilySchema } from "./family";
import { StudentSchema } from "./student";
import { StudentDocumentSchema } from "./student-document";
import { CommunicationSchema } from "./communication";

const MoneyCentsSchema = z.number().int();
const NullableMoneyCentsSchema = z.number().int().nullable();

export const AdmissionsTuitionMoneyFieldSchema = z
  .object({
    currentCents: MoneyCentsSchema,
    defaultCents: MoneyCentsSchema,
    overridden: z.boolean().default(false),
    note: z.string().nullable().optional(),
  })
  .strict();
export type AdmissionsTuitionMoneyField = z.infer<
  typeof AdmissionsTuitionMoneyFieldSchema
>;

export const AdmissionsTuitionFeeLineSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    rateDescription: z.string().nullable().optional(),
    amount: AdmissionsTuitionMoneyFieldSchema,
    dueDate: z.string().nullable().optional(),
    defaultDueDate: z.string().nullable().optional(),
    exemptReason: z.string().nullable().optional(),
    perFamilyAllocatedHere: z.boolean().default(false),
    includedInChildName: z.string().nullable().optional(),
  })
  .strict();
export type AdmissionsTuitionFeeLine = z.infer<
  typeof AdmissionsTuitionFeeLineSchema
>;

export const AdmissionsTuitionPercentDiscountSchema = z
  .object({
    eligible: z.boolean().default(false),
    enabled: z.boolean().default(false),
    percentBasisPoints: z.number().int().nonnegative().default(0),
    defaultPercentBasisPoints: z.number().int().nonnegative().default(0),
    overridden: z.boolean().default(false),
    amountCents: MoneyCentsSchema.default(0),
    defaultAmountCents: MoneyCentsSchema.default(0),
  })
  .strict();
export type AdmissionsTuitionPercentDiscount = z.infer<
  typeof AdmissionsTuitionPercentDiscountSchema
>;

export const AdmissionsTuitionFundraisingSchema = z
  .object({
    enabled: z.boolean().default(false),
    exempt: z.boolean().default(false),
    amount: AdmissionsTuitionMoneyFieldSchema,
  })
  .strict();
export type AdmissionsTuitionFundraising = z.infer<
  typeof AdmissionsTuitionFundraisingSchema
>;

export const AdmissionsTuitionChildConfigurationSchema = z
  .object({
    childApplicationId: z.string().min(1),
    studentId: z.string().nullable().optional(),
    name: z.string().min(1),
    gradeLabel: z.string().min(1),
    gradeKey: z.string().min(1),
    divisionLabel: z.string().min(1),
    gender: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    baseTuition: AdmissionsTuitionMoneyFieldSchema,
    fees: z.array(AdmissionsTuitionFeeLineSchema).default([]),
    multiChildDiscount: AdmissionsTuitionMoneyFieldSchema,
    staffDiscount: AdmissionsTuitionPercentDiscountSchema,
    chinuchDiscount: AdmissionsTuitionPercentDiscountSchema,
    combinedDiscount: AdmissionsTuitionMoneyFieldSchema,
    financialAidCredit: AdmissionsTuitionMoneyFieldSchema,
    fundraising: AdmissionsTuitionFundraisingSchema,
    subtotalCents: MoneyCentsSchema,
    defaultSubtotalCents: MoneyCentsSchema,
  })
  .strict();
export type AdmissionsTuitionChildConfiguration = z.infer<
  typeof AdmissionsTuitionChildConfigurationSchema
>;

export const AdmissionsTuitionTotalsSchema = z
  .object({
    tuitionSubtotalCents: MoneyCentsSchema,
    multiChildDiscountCents: MoneyCentsSchema,
    staffChinuchDiscountCents: MoneyCentsSchema,
    totalTuitionCents: MoneyCentsSchema,
    totalFeesCents: MoneyCentsSchema,
    financialAidCreditCents: MoneyCentsSchema,
    fundraisingObligationCents: MoneyCentsSchema,
    totalObligationCents: MoneyCentsSchema,
  })
  .strict();
export type AdmissionsTuitionTotals = z.infer<
  typeof AdmissionsTuitionTotalsSchema
>;

export const AdmissionsTuitionInstallmentPreviewSchema = z
  .object({
    paymentPlanId: z.string().nullable(),
    paymentPlanLabel: z.string().nullable(),
    installmentCount: z.number().int().nonnegative(),
    installmentAmountCents: MoneyCentsSchema,
    finalInstallmentAmountCents: MoneyCentsSchema,
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    previewLabel: z.string().nullable(),
  })
  .strict();
export type AdmissionsTuitionInstallmentPreview = z.infer<
  typeof AdmissionsTuitionInstallmentPreviewSchema
>;

export const AdmissionsTuitionContractStatusSchema = z.enum([
  "not_generated",
  "draft",
  "sent",
]);
export type AdmissionsTuitionContractStatus = z.infer<
  typeof AdmissionsTuitionContractStatusSchema
>;

export const AdmissionsTuitionContractVersionStatusSchema = z.enum([
  "draft",
  "sent",
  "signed",
]);
export type AdmissionsTuitionContractVersionStatus = z.infer<
  typeof AdmissionsTuitionContractVersionStatusSchema
>;

export const AdmissionsTuitionContractVersionSchema = z
  .object({
    id: z.string().min(1),
    version: z.string().min(1),
    status: AdmissionsTuitionContractVersionStatusSchema,
    documentId: z.string().nullable().optional(),
    resourceId: z.string().nullable().optional(),
    generatedAt: z.string().nullable().optional(),
    sentAt: z.string().nullable().optional(),
    signedAt: z.string().nullable().optional(),
    signedDocumentId: z.string().nullable().optional(),
    signedDocumentResourceId: z.string().nullable().optional(),
    signerFullName: z.string().nullable().optional(),
    supersedesVersion: z.string().nullable().optional(),
    contractModel: z.unknown().nullable().optional(),
  })
  .strict();
export type AdmissionsTuitionContractVersion = z.infer<
  typeof AdmissionsTuitionContractVersionSchema
>;

export const AdmissionsTuitionContractSnapshotSchema = z
  .object({
    status: AdmissionsTuitionContractStatusSchema.default("not_generated"),
    documentId: z.string().nullable().optional(),
    resourceId: z.string().nullable().optional(),
    generatedAt: z.string().nullable().optional(),
    sentAt: z.string().nullable().optional(),
    version: z.string().nullable().optional(),
    versions: z.array(AdmissionsTuitionContractVersionSchema).default([]),
  })
  .strict();
export type AdmissionsTuitionContractSnapshot = z.infer<
  typeof AdmissionsTuitionContractSnapshotSchema
>;

export const AdmissionsTuitionConfigurationSchema = z
  .object({
    version: z.literal("1"),
    familyId: z.string().min(1),
    applicationId: z.string().min(1),
    academicYear: z.string().min(1),
    rateCategoryId: z.string().min(1),
    rateCategoryLabel: z.string().min(1),
    admissionsSource: z.boolean().default(true),
    configurationComplete: z.boolean().default(false),
    updatedAt: z.string().nullable().optional(),
    children: z.array(AdmissionsTuitionChildConfigurationSchema).default([]),
    totals: AdmissionsTuitionTotalsSchema,
    paymentPlan: AdmissionsTuitionInstallmentPreviewSchema,
    contract: AdmissionsTuitionContractSnapshotSchema.default({
      status: "not_generated",
      versions: [],
    }),
  })
  .strict();
export type AdmissionsTuitionConfiguration = z.infer<
  typeof AdmissionsTuitionConfigurationSchema
>;

export const AdmissionsTuitionConfigurationEnvelopeSchema = z
  .object({
    configuration: AdmissionsTuitionConfigurationSchema.nullable(),
    documentId: z.string().nullable().optional(),
  })
  .strict();
export type AdmissionsTuitionConfigurationEnvelope = z.infer<
  typeof AdmissionsTuitionConfigurationEnvelopeSchema
>;

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableStringify(entry)).join(",")}]`;
  }
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .filter((key) => typeof record[key] !== "undefined")
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(",")}}`;
};

const hashTuitionApprovalPayload = (payload: string): string => {
  let hash = 2166136261;
  for (let index = 0; index < payload.length; index += 1) {
    hash ^= payload.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

const LEGACY_SENT_APPROVAL_COMPATIBILITY_CUTOFF_ENV_KEY =
  "TRIEDGE_ADMISSIONS_TUITION_LEGACY_SENT_APPROVAL_CUTOFF";
const DEFAULT_LEGACY_SENT_APPROVAL_COMPATIBILITY_CUTOFF =
  "2026-05-06T00:00:00.000Z";
const DEFAULT_LEGACY_SENT_APPROVAL_COMPATIBILITY_CUTOFF_MS = Date.parse(
  DEFAULT_LEGACY_SENT_APPROVAL_COMPATIBILITY_CUTOFF,
);
const legacySentApprovalCutoffWarnings = new Set<string>();

const resolveLegacySentApprovalCompatibilityCutoffMs = (): number => {
  const runtime = globalThis as unknown as {
    [LEGACY_SENT_APPROVAL_COMPATIBILITY_CUTOFF_ENV_KEY]?: unknown;
    process?: {
      env?: Record<string, string | undefined>;
    };
  };
  const configuredCutoff = String(
    runtime[LEGACY_SENT_APPROVAL_COMPATIBILITY_CUTOFF_ENV_KEY] ??
      runtime.process?.env?.[
        LEGACY_SENT_APPROVAL_COMPATIBILITY_CUTOFF_ENV_KEY
      ] ??
      "",
  ).trim();
  if (!configuredCutoff) {
    return DEFAULT_LEGACY_SENT_APPROVAL_COMPATIBILITY_CUTOFF_MS;
  }

  const configuredEpochMs = Date.parse(configuredCutoff);
  return Number.isFinite(configuredEpochMs)
    ? configuredEpochMs
    : DEFAULT_LEGACY_SENT_APPROVAL_COMPATIBILITY_CUTOFF_MS;
};

const warnLegacySentApprovalCutoffBlocked = (
  generatedAt: string,
  cutoffMs: number,
): void => {
  const warningKey = `${generatedAt}:${cutoffMs}`;
  if (legacySentApprovalCutoffWarnings.has(warningKey)) {
    return;
  }
  legacySentApprovalCutoffWarnings.add(warningKey);
  console.warn(
    "[admissions-tuition] legacy sent contract visibility blocked by compatibility cutoff",
    {
      generatedAt,
      cutoff: new Date(cutoffMs).toISOString(),
      overrideEnv: LEGACY_SENT_APPROVAL_COMPATIBILITY_CUTOFF_ENV_KEY,
    },
  );
};

export const buildAdmissionsTuitionApprovalVersion = (
  configuration: AdmissionsTuitionConfiguration,
): string => {
  const {
    contract: _contract,
    updatedAt: _updatedAt,
    ...approvalPayload
  } = configuration as AdmissionsTuitionConfiguration & Record<string, unknown>;
  return `tuition-v1-${hashTuitionApprovalPayload(stableStringify(approvalPayload))}`;
};

export const resolveAdmissionsTuitionApprovalState = (
  configuration: AdmissionsTuitionConfiguration,
): {
  currentVersion: string;
  approved: boolean;
  parentVisible: boolean;
} => {
  const currentVersion = buildAdmissionsTuitionApprovalVersion(configuration);
  const contractVersion = String(configuration.contract.version ?? "").trim();
  const generatedAt = String(configuration.contract.generatedAt ?? "").trim();
  const generatedAtEpochMs = Date.parse(generatedAt);
  const legacyCutoffMs = resolveLegacySentApprovalCompatibilityCutoffMs();
  const legacyIsoSentContract =
    configuration.contract.status === "sent" &&
    /^\d{4}-\d{2}-\d{2}T/.test(contractVersion) &&
    contractVersion === generatedAt &&
    Number.isFinite(generatedAtEpochMs);
  const legacyGeneratedSentContract =
    legacyIsoSentContract && generatedAtEpochMs < legacyCutoffMs;
  if (legacyIsoSentContract && !legacyGeneratedSentContract) {
    warnLegacySentApprovalCutoffBlocked(generatedAt, legacyCutoffMs);
  }
  const approved =
    contractVersion === currentVersion || legacyGeneratedSentContract;
  const hasSentVersion = (configuration.contract.versions ?? []).some(
    (entry) =>
      (entry.status === "sent" || entry.status === "signed") &&
      Boolean(
        String(entry.documentId ?? "").trim() ||
          String(entry.resourceId ?? "").trim(),
      ),
  );
  return {
    currentVersion,
    approved,
    parentVisible:
      (configuration.contract.status === "sent" && approved) || hasSentVersion,
  };
};

export const isAdmissionsTuitionConfigurationApproved = (
  configuration: AdmissionsTuitionConfiguration,
): boolean => resolveAdmissionsTuitionApprovalState(configuration).approved;

export const isAdmissionsTuitionConfigurationParentVisible = (
  configuration: AdmissionsTuitionConfiguration,
): boolean =>
  resolveAdmissionsTuitionApprovalState(configuration).parentVisible;

export const getAdmissionsTuitionContractVersions = (
  configuration: AdmissionsTuitionConfiguration,
): AdmissionsTuitionContractVersion[] => {
  const versions = [...(configuration.contract.versions ?? [])];
  const topLevelVersion = String(configuration.contract.version ?? "").trim();
  const topLevelDocumentId = String(
    configuration.contract.documentId ?? "",
  ).trim();
  const hasTopLevelInLedger = versions.some(
    (entry) => entry.version === topLevelVersion,
  );
  if (topLevelVersion && topLevelDocumentId && !hasTopLevelInLedger) {
    versions.push({
      id: topLevelVersion,
      version: topLevelVersion,
      status: configuration.contract.status === "sent" ? "sent" : "draft",
      documentId: topLevelDocumentId,
      resourceId: configuration.contract.resourceId ?? null,
      generatedAt: configuration.contract.generatedAt ?? null,
      sentAt: configuration.contract.sentAt ?? null,
      signedAt: null,
      signedDocumentId: null,
      signedDocumentResourceId: null,
      signerFullName: null,
      supersedesVersion: null,
      contractModel: null,
    });
  }
  return versions;
};

export const getLatestAdmissionsTuitionContractVersion = (
  configuration: AdmissionsTuitionConfiguration,
  statuses: AdmissionsTuitionContractVersionStatus[] = [
    "draft",
    "sent",
    "signed",
  ],
): AdmissionsTuitionContractVersion | null => {
  const allowedStatuses = new Set(statuses);
  const versions = getAdmissionsTuitionContractVersions(configuration)
    .filter((entry) => allowedStatuses.has(entry.status))
    .sort((left, right) => {
      const leftTime =
        Date.parse(String(left.sentAt ?? left.generatedAt ?? "")) || 0;
      const rightTime =
        Date.parse(String(right.sentAt ?? right.generatedAt ?? "")) || 0;
      return rightTime - leftTime;
    });
  return versions[0] ?? null;
};

export type AdmissionsTuitionContractSchoolInfo = {
  schoolName?: string | null;
  schoolLogo?: string | null;
  schoolAddress?: string | null;
  schoolPhone?: string | null;
  schoolEmail?: string | null;
};

export type AdmissionsTuitionContractViewModel = {
  schoolName: string;
  schoolLogo: string | null;
  schoolAddress: string;
  schoolPhone: string;
  schoolEmail: string;
  academicYear: string;
  currentDateLabel: string;
  parentSalutation: string;
  familyAddressLines: string[];
  studentRows: Array<{
    id: string;
    name: string;
    gradeLabel: string;
    tuitionLabel: string;
  }>;
  feeRows: Array<{
    id: string;
    label: string;
    scheduleLabel: string;
    amountLabel: string;
  }>;
  tuitionSubtotalLabel: string;
  multiChildDiscountLabel: string;
  staffChinuchDiscountLabel: string;
  totalTuitionLabel: string;
  totalFeesLabel: string;
  financialAidCreditLabel: string;
  fundraisingObligationLabel: string;
  totalObligationLabel: string;
  paymentPlanLabel: string;
  installmentSummaryLabel: string;
};

export const formatAdmissionsTuitionContractCurrency = (
  cents: number,
): string =>
  `$${(Math.abs(Number(cents) || 0) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const formatAdmissionsTuitionContractDate = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const localDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const parsed = localDateMatch
    ? new Date(
        Number(localDateMatch[1]),
        Number(localDateMatch[2]) - 1,
        Number(localDateMatch[3]),
      )
    : new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  return parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const toContractTitleCase = (value: string): string =>
  value.replace(/\b\w/g, (char) => char.toUpperCase());

export const formatAdmissionsTuitionContractGradeLabel = (
  value: unknown,
): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "Grade on file";
  const normalized = raw.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  const withoutGradePrefix = normalized.replace(/^grade\s*/i, "").trim();
  if (!withoutGradePrefix) return "Grade on file";
  if (/^\d+$/.test(withoutGradePrefix))
    return `Grade ${String(Number(withoutGradePrefix))}`;
  if (/^k(?:indergarten)?$/i.test(withoutGradePrefix)) return "Kindergarten";
  if (/^pre\s*k$/i.test(withoutGradePrefix)) return "Pre-K";
  if (/^nursery$/i.test(withoutGradePrefix)) return "Nursery";
  if (/^preschool$/i.test(withoutGradePrefix)) return "Preschool";
  return normalized.toLowerCase().startsWith("grade ")
    ? `Grade ${toContractTitleCase(withoutGradePrefix)}`
    : toContractTitleCase(normalized);
};

const buildContractFeeScheduleLabel = (
  fee: AdmissionsTuitionFeeLine,
): string => {
  const dueDate = formatAdmissionsTuitionContractDate(
    fee.dueDate ?? fee.defaultDueDate,
  );
  if (dueDate) return `Due ${dueDate}`;
  return (
    String(fee.rateDescription ?? "").trim() || "School office schedule applies"
  );
};

export const buildAdmissionsTuitionContractViewModel = (input: {
  configuration: AdmissionsTuitionConfiguration;
  parentSalutation: string;
  familyAddressLines: string[];
  schoolInfo?: AdmissionsTuitionContractSchoolInfo | null;
  generatedAt?: Date | string | null;
}): AdmissionsTuitionContractViewModel => {
  const schoolInfo = input.schoolInfo ?? {};
  const configuration = input.configuration;
  const installmentCount = Number(
    configuration.paymentPlan.installmentCount ?? 0,
  );
  const installmentAmount = Number(
    configuration.paymentPlan.installmentAmountCents ?? 0,
  );
  const finalInstallmentAmount = Number(
    configuration.paymentPlan.finalInstallmentAmountCents ?? 0,
  );
  const installmentRange = [
    formatAdmissionsTuitionContractDate(configuration.paymentPlan.startDate),
    formatAdmissionsTuitionContractDate(configuration.paymentPlan.endDate),
  ]
    .filter(Boolean)
    .join(" to ");
  const installmentSummaryLabel =
    installmentCount > 0 && installmentAmount > 0
      ? finalInstallmentAmount !== installmentAmount && installmentCount > 1
        ? `${installmentCount - 1} payments of ${formatAdmissionsTuitionContractCurrency(installmentAmount)} and a final payment of ${formatAdmissionsTuitionContractCurrency(finalInstallmentAmount)}${installmentRange ? ` from ${installmentRange}` : ""}`
        : `${installmentCount} payments of ${formatAdmissionsTuitionContractCurrency(installmentAmount)}${installmentRange ? ` from ${installmentRange}` : ""}`
      : "Payment schedule not selected";

  return {
    schoolName: String(schoolInfo.schoolName ?? "").trim() || "School",
    schoolLogo: String(schoolInfo.schoolLogo ?? "").trim() || null,
    schoolAddress: String(schoolInfo.schoolAddress ?? "").trim(),
    schoolPhone: String(schoolInfo.schoolPhone ?? "").trim(),
    schoolEmail: String(schoolInfo.schoolEmail ?? "").trim(),
    academicYear: configuration.academicYear,
    currentDateLabel: formatAdmissionsTuitionContractDate(
      input.generatedAt ?? new Date(),
    ),
    parentSalutation: input.parentSalutation || "Family",
    familyAddressLines: input.familyAddressLines.filter(Boolean),
    studentRows: configuration.children.map((child) => ({
      id: child.childApplicationId || child.name,
      name: child.name || "Student",
      gradeLabel: formatAdmissionsTuitionContractGradeLabel(child.gradeLabel),
      tuitionLabel: formatAdmissionsTuitionContractCurrency(
        child.baseTuition.currentCents,
      ),
    })),
    feeRows: configuration.children.flatMap((child) =>
      (child.fees ?? [])
        .filter((fee) => fee.amount.currentCents > 0)
        .map((fee) => ({
          id: `${child.childApplicationId}-${fee.id}`,
          label: fee.name || "Configured fee",
          scheduleLabel: buildContractFeeScheduleLabel(fee),
          amountLabel: formatAdmissionsTuitionContractCurrency(
            fee.amount.currentCents,
          ),
        })),
    ),
    tuitionSubtotalLabel: formatAdmissionsTuitionContractCurrency(
      configuration.totals.tuitionSubtotalCents,
    ),
    multiChildDiscountLabel: formatAdmissionsTuitionContractCurrency(
      configuration.totals.multiChildDiscountCents,
    ),
    staffChinuchDiscountLabel: formatAdmissionsTuitionContractCurrency(
      configuration.totals.staffChinuchDiscountCents,
    ),
    totalTuitionLabel: formatAdmissionsTuitionContractCurrency(
      configuration.totals.totalTuitionCents,
    ),
    totalFeesLabel: formatAdmissionsTuitionContractCurrency(
      configuration.totals.totalFeesCents,
    ),
    financialAidCreditLabel: formatAdmissionsTuitionContractCurrency(
      configuration.totals.financialAidCreditCents,
    ),
    fundraisingObligationLabel: formatAdmissionsTuitionContractCurrency(
      configuration.totals.fundraisingObligationCents,
    ),
    totalObligationLabel: formatAdmissionsTuitionContractCurrency(
      configuration.totals.totalObligationCents,
    ),
    paymentPlanLabel:
      configuration.paymentPlan.paymentPlanLabel || "Selected payment plan",
    installmentSummaryLabel,
  };
};

export const AdmissionsTuitionResolvedChildSchema = z
  .object({
    childApplicationId: z.string().min(1),
    studentId: z.string().nullable().optional(),
    name: z.string().min(1),
    gradeLabel: z.string().min(1),
    gradeKey: z.string().min(1),
    divisionLabel: z.string().min(1),
    gender: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    sortOrder: z.number().int().nonnegative().default(0),
  })
  .strict();
export type AdmissionsTuitionResolvedChild = z.infer<
  typeof AdmissionsTuitionResolvedChildSchema
>;

export const AdmissionsTuitionResolutionSchema = z
  .object({
    applicationId: z.string().min(1),
    academicYear: z.string().min(1),
    currentStatus: z.string().nullable().optional(),
    family: FamilySchema,
    students: z.array(StudentSchema).default([]),
    acceptedChildren: z.array(AdmissionsTuitionResolvedChildSchema).default([]),
  })
  .strict();
export type AdmissionsTuitionResolution = z.infer<
  typeof AdmissionsTuitionResolutionSchema
>;

export const AdmissionsTuitionContractGenerationSchema = z
  .object({
    configuration: AdmissionsTuitionConfigurationSchema,
    contractDocument: StudentDocumentSchema,
  })
  .strict();
export type AdmissionsTuitionContractGeneration = z.infer<
  typeof AdmissionsTuitionContractGenerationSchema
>;

export const AdmissionsTuitionContractSendSchema = z
  .object({
    configuration: AdmissionsTuitionConfigurationSchema,
    contractDocument: StudentDocumentSchema,
    communication: CommunicationSchema.nullable(),
  })
  .strict();
export type AdmissionsTuitionContractSend = z.infer<
  typeof AdmissionsTuitionContractSendSchema
>;

export const AdmissionsTuitionInstallmentScheduleEntrySchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    amountCents: MoneyCentsSchema,
    remainingAmountCents: MoneyCentsSchema,
    dueDate: z.string().nullable(),
    status: z.enum(["paid", "due", "overdue", "upcoming"]),
  })
  .strict();
export type AdmissionsTuitionInstallmentScheduleEntry = z.infer<
  typeof AdmissionsTuitionInstallmentScheduleEntrySchema
>;

export const AdmissionsTuitionProjectionSchema = z
  .object({
    planLabel: z.string().nullable(),
    installmentSchedule: z
      .array(AdmissionsTuitionInstallmentScheduleEntrySchema)
      .default([]),
    totalObligationCents: NullableMoneyCentsSchema,
  })
  .strict();
export type AdmissionsTuitionProjection = z.infer<
  typeof AdmissionsTuitionProjectionSchema
>;
